'use client';

import { MaterialSymbol } from '@florencelabs/icons';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { useOrgContext } from '@provider-directory/components/org-context/org-context-provider';
import { PhoneLink } from '@provider-directory/components/phone-number';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import { getLocationDisplayInfo } from '@provider-directory/functions/location';
import { ProviderDetailsResource } from '@provider-directory/types/providers.contracts';

export const ProviderSummaryPhoneLink = ({
  provider,
}: {
  provider: ProviderDetailsResource;
}): ReactElement | null => {
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const orgContext = useOrgContext();

  // The back-end always returns the primary location first.
  const primaryLocation = provider.locations[0] ?? null;
  const qaIdPrefix = `provider-${provider.npi}`;

  const phoneNumber = getAvailablePhoneNumber(
    primaryLocation,
    provider,
    orgContext.getAssociationPhoneSelection(),
  );

  if (!phoneNumber) {
    return null;
  }

  const displayName = primaryLocation
    ? getLocationDisplayInfo(primaryLocation).displayName
    : null;

  return (
    <div>
      <div className={'pb-2 text-body-medium-semibold'}>
        {t('details.phone')}
      </div>
      <PhoneLink
        phone={phoneNumber}
        format={'stripped'}
        onClick={(e) => {
          e.preventDefault();
          analytics.calledEvent({
            provider,
            phoneNumber,
            linkContextPage: 'ProviderDetails',
            linkContextComponent: 'ProviderHeader',
            clickText: phoneNumber,
            clickURL: e.currentTarget.href,
          });
          window.open(e.currentTarget.href, '_self');
        }}
        data-testid={`${qaIdPrefix}-phone-link`}
        aria-label={
          displayName
            ? t('aria.callLocation', { locationName: displayName })
            : t('aria.callProvider', { providerName: provider.name.full })
        }
        className={'flex flex-row items-center gap-2 text-primary-color'}
      >
        {({ formattedPhone }) => (
          <>
            <div className={'whitespace-nowrap text-body-medium-semibold'}>
              {formattedPhone}
            </div>
            <MaterialSymbol
              name={'north_east'}
              size={16}
              aria-hidden={'true'}
            />
          </>
        )}
      </PhoneLink>
    </div>
  );
};
